import { useState, useEffect, useCallback, useRef } from 'react';

import { getPaymentStatus } from '../../../../api/paymentService';
import {
  PAYMENT_POLLING_INTERVAL_MS,
  PAYMENT_POLLING_MAX_ATTEMPTS,
} from '../../../../constants/payment';
import type { PaymentStatus, GetPaymentStatusResponse } from '../../../../types/payment';

/**
 * Terminal payment statuses that should stop polling
 */
const TERMINAL_STATUSES: PaymentStatus[] = [
  'CONFIRMED',
  'FAILED',
  'EXPIRED',
  'REFUNDED',
  'CHARGEBACKED',
  'USED',
];

/**
 * Backoff configuration
 */
const BACKOFF_CONFIG = {
  initialIntervalMs: 2000,
  maxIntervalMs: 10000,
  multiplier: 1.5,
  maxConsecutiveErrors: 5,
};

/**
 * Options for usePaymentPolling hook
 */
export interface UsePaymentPollingOptions {
  /** Payment ID to poll */
  paymentId: string;
  /** Initial payment status */
  initialStatus?: PaymentStatus;
  /** Whether polling is enabled */
  enabled?: boolean;
  /** Polling interval in milliseconds */
  intervalMs?: number;
  /** Maximum number of poll attempts */
  maxAttempts?: number;
  /** Callback when status changes */
  onStatusChange?: (status: PaymentStatus) => void;
  /** Callback when payment data is received */
  onPaymentUpdate?: (payment: GetPaymentStatusResponse) => void;
  /** Callback on polling error */
  onError?: (error: Error) => void;
}

/**
 * Result returned by usePaymentPolling hook
 */
export interface UsePaymentPollingResult {
  /** Current payment status */
  currentStatus: PaymentStatus;
  /** Latest payment data */
  payment: GetPaymentStatusResponse | null;
  /** Whether polling is active */
  isPolling: boolean;
  /** Number of poll attempts */
  pollCount: number;
  /** Last polling error */
  error: Error | null;
  /** Start polling manually */
  startPolling: () => void;
  /** Stop polling manually */
  stopPolling: () => void;
}

/**
 * Check if status is terminal (should stop polling)
 */
const isTerminalStatus = (status: PaymentStatus): boolean =>
  TERMINAL_STATUSES.includes(status);

/**
 * usePaymentPolling Hook
 * 
 * Polls payment status at regular intervals until a terminal status is reached.
 * Automatically starts polling for PENDING status and stops for terminal statuses.
 * 
 * Task 9.3: Real-time status updates via polling
 */
export function usePaymentPolling(
  options: UsePaymentPollingOptions
): UsePaymentPollingResult {
  const {
    paymentId,
    initialStatus = 'PENDING',
    enabled = true,
    intervalMs = PAYMENT_POLLING_INTERVAL_MS,
    maxAttempts = PAYMENT_POLLING_MAX_ATTEMPTS,
    onStatusChange,
    onPaymentUpdate,
    onError,
  } = options;

  const [currentStatus, setCurrentStatus] = useState<PaymentStatus>(initialStatus);
  const [payment, setPayment] = useState<GetPaymentStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Refs for cleanup and tracking
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const pollCountRef = useRef(0);
  const consecutiveErrorsRef = useRef(0);
  const currentIntervalRef = useRef(intervalMs);

  /**
   * Perform a single poll
   */
  const poll = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const response = await getPaymentStatus(paymentId);
      
      if (!mountedRef.current) return;

      // Clear any previous error and reset backoff
      setError(null);
      consecutiveErrorsRef.current = 0;
      currentIntervalRef.current = intervalMs;

      // Update payment data
      setPayment(response);
      onPaymentUpdate?.(response);

      // Check for status change
      const newStatus = response.status;
      if (newStatus !== currentStatus) {
        setCurrentStatus(newStatus);
        onStatusChange?.(newStatus);

        // Stop polling if terminal status reached
        if (isTerminalStatus(newStatus)) {
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }

      // Increment poll count
      pollCountRef.current += 1;
      setPollCount(pollCountRef.current);

      // Check max attempts
      if (pollCountRef.current >= maxAttempts) {
        setIsPolling(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;

      const pollError = err instanceof Error ? err : new Error('Polling failed');
      setError(pollError);
      onError?.(pollError);

      // Increment consecutive errors and apply backoff
      consecutiveErrorsRef.current += 1;
      
      // Calculate new interval with exponential backoff
      const newInterval = Math.min(
        currentIntervalRef.current * BACKOFF_CONFIG.multiplier,
        BACKOFF_CONFIG.maxIntervalMs
      );
      currentIntervalRef.current = newInterval;

      // Increment poll count even on error
      pollCountRef.current += 1;
      setPollCount(pollCountRef.current);

      // Stop if too many consecutive errors or max attempts reached
      if (
        consecutiveErrorsRef.current >= BACKOFF_CONFIG.maxConsecutiveErrors ||
        pollCountRef.current >= maxAttempts
      ) {
        setIsPolling(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, [paymentId, currentStatus, maxAttempts, intervalMs, onStatusChange, onPaymentUpdate, onError]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Already polling
    if (isTerminalStatus(currentStatus)) return; // Don't poll terminal statuses

    setIsPolling(true);
    intervalRef.current = setInterval(poll, intervalMs);
  }, [currentStatus, intervalMs, poll]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Pause polling (e.g., when tab is hidden)
   */
  const pausePolling = useCallback(() => {
    if (!isPolling) return;
    setIsPaused(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isPolling]);

  /**
   * Resume polling (e.g., when tab becomes visible)
   */
  const resumePolling = useCallback(() => {
    if (!isPaused) return;
    setIsPaused(false);
    if (!isTerminalStatus(currentStatus)) {
      intervalRef.current = setInterval(poll, currentIntervalRef.current);
    }
  }, [isPaused, currentStatus, poll]);

  // Handle visibility change - pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pausePolling();
      } else {
        resumePolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pausePolling, resumePolling]);

  // Auto-start polling when enabled and status is non-terminal
  useEffect(() => {
    mountedRef.current = true;

    if (enabled && !isTerminalStatus(initialStatus)) {
      startPolling();
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, initialStatus, startPolling]);

  // Update status when initialStatus prop changes
  useEffect(() => {
    setCurrentStatus(initialStatus);
    
    // Stop polling if new status is terminal
    if (isTerminalStatus(initialStatus)) {
      stopPolling();
    }
  }, [initialStatus, stopPolling]);

  return {
    currentStatus,
    payment,
    isPolling,
    pollCount,
    error,
    startPolling,
    stopPolling,
  };
}

export default usePaymentPolling;
